import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Text,
} from "@microbeta/ui";
import type React from "react";
import { useTranslation } from "react-i18next";
import { MdCloudOff, MdHd, MdHighlightOff, MdStar } from "react-icons/md";
import styles from "./paywall-modal.module.scss";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}

export const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={styles.modalContent}>
        <div className={styles.header}>
          <div className={styles.iconContainer}>
            <MdStar className={styles.starIcon} />
          </div>
          <DialogTitle className={styles.title}>
            {t("settings.subscription.upgradeToPremium")}
          </DialogTitle>
          <Text variant="medium" className={styles.subtitle}>
            {t(
              "paywall.description",
              "Unlock the full potential of Microbeta with a Premium subscription."
            )}
          </Text>
        </div>

        <div className={styles.benefits}>
          <div className={styles.benefitItem}>
            <div className={styles.benefitIcon}>
              <MdHd />
            </div>
            <div className={styles.benefitText}>
              <Text variant="small" weight="bold">
                {t("settings.subscription.highResModels")}
              </Text>
              <Text variant="small" className={styles.benefitDescription}>
                {t(
                  "paywall.benefits.hdModelsDesc",
                  "View models in stunning detail with enhanced quality and textures."
                )}
              </Text>
            </div>
          </div>

          <div className={styles.benefitItem}>
            <div className={styles.benefitIcon}>
              <MdHighlightOff />
            </div>
            <div className={styles.benefitText}>
              <Text variant="small" weight="bold">
                {t("settings.subscription.ads")}
              </Text>
              <Text variant="small" className={styles.benefitDescription}>
                {t(
                  "paywall.benefits.noAdsDesc",
                  "Enjoy an uninterrupted experience with zero advertisements."
                )}
              </Text>
            </div>
          </div>

          <div className={styles.benefitItem}>
            <div className={styles.benefitIcon}>
              <MdCloudOff />
            </div>
            <div className={styles.benefitText}>
              <Text variant="small" weight="bold">
                {t("settings.subscription.offlineSupport")}
              </Text>
              <Text variant="small" className={styles.benefitDescription}>
                {t(
                  "paywall.benefits.offlineModeDesc",
                  "Download models and route info for offline use in our mobile apps."
                )}
              </Text>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            type="button"
            variant="primary"
            size="large"
            onClick={onUpgrade}
            className={styles.upgradeButton}
          >
            {t("paywall.upgradeButton", "Upgrade Now")}
          </Button>
          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className={styles.closeButton}
            >
              {t("common.cancel")}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};
